import type { ConsoleLogEntry } from "@ark-snitch/shared";

const MAX_ENTRIES = 100;
const MAX_ARG_CHARS = 2000;

const buffer: ConsoleLogEntry[] = [];
let installed = false;

const safeStringify = (v: unknown): string => {
  if (typeof v === "string") return v;
  if (v instanceof Error) return `${v.name}: ${v.message}\n${v.stack ?? ""}`;
  try {
    const seen = new WeakSet();
    return JSON.stringify(v, (_k, val) => {
      if (typeof val === "object" && val !== null) {
        if (seen.has(val)) return "[Circular]";
        seen.add(val);
      }
      if (typeof val === "function") return `[Function ${val.name || "anonymous"}]`;
      if (typeof val === "bigint") return `${val.toString()}n`;
      if (val instanceof Element) {
        const id = val.id ? `#${val.id}` : "";
        return `<${val.tagName.toLowerCase()}${id}>`;
      }
      return val;
    });
  } catch {
    try {
      return String(v);
    } catch {
      return "[unserializable]";
    }
  }
};

const truncate = (s: string) =>
  s.length > MAX_ARG_CHARS ? `${s.slice(0, MAX_ARG_CHARS)}…[truncated]` : s;

export const installConsoleBuffer = (): void => {
  if (installed) return;
  installed = true;

  const levels: ConsoleLogEntry["level"][] = [
    "log",
    "info",
    "warn",
    "error",
    "debug",
  ];

  for (const level of levels) {
    try {
      const original = console[level].bind(console);
      console[level] = (...args: unknown[]) => {
        try {
          buffer.push({
            level,
            ts: Date.now(),
            args: args.map((a) => truncate(safeStringify(a))),
          });
          if (buffer.length > MAX_ENTRIES) {
            buffer.splice(0, buffer.length - MAX_ENTRIES);
          }
        } catch {
          // never let logging instrumentation break the host app
        }
        original(...args);
      };
    } catch {
      // host froze console.<level> (e.g. oidc-spa browserRuntimeFreeze)
      // — skip this level, others may still wrap successfully
    }
  }

  window.addEventListener("error", (e) => {
    buffer.push({
      level: "error",
      ts: Date.now(),
      args: [
        truncate(
          `Uncaught ${e.message} at ${e.filename}:${e.lineno}:${e.colno}\n${e.error?.stack ?? ""}`,
        ),
      ],
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    buffer.push({
      level: "error",
      ts: Date.now(),
      args: [truncate(`Unhandled promise rejection: ${safeStringify(e.reason)}`)],
    });
  });
};

export const snapshotConsoleBuffer = (): ConsoleLogEntry[] => buffer.slice();
